import {clamp} from '@benchling/js-utils';

import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationAxisScale as AxisScale,
  AnalyticsVisualizationBarChartType as BarChartType,
  AnalyticsVisualizationGradientDirection as GradientDirection,
  AnalyticsVisualizationLegendLocation as LegendLocation,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {SCATTER_PLOT_DEFAULT_COLOR_PALETTE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import pick from 'util/pick';

import {
  BaseChartParameters,
  Continuous2DChartParameters,
  isVisualizationTypeParameters,
  VisualizationType,
  VisualizationTypeToParameters,
} from '../AnalyticsVisualizationTypes';

type AnalyticsVisualizationParameters = VisualizationConfig['parameters'];

type BarChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationBarChartParametersNode'
>;

type HeatmapParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationHeatmapParametersNode'
>;

type LineChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationLineChartParametersNode'
>;

type PieChartParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationPieChartParametersNode'
>;

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

type TableParameters = ExtractByTypename<
  AnalyticsVisualizationParameters,
  'AnalyticsVisualizationTableParametersNode'
>;

type Update<TParameters extends AnalyticsVisualizationParameters> = Partial<
  Omit<AnalyticsVisualization, 'parameters'> & {parameters: TParameters}
>;

abstract class VisualizationTypeSetter<TType extends VisualizationType> {
  columnNames: Array<DataFrameColumnDef>;
  previousVisualization: AnalyticsVisualization;
  useSystemNames: boolean;
  enableAggregation: boolean;
  enableErrorBarColumn: boolean;

  constructor(
    columnNames: Array<DataFrameColumnDef>,
    previousVisualization: AnalyticsVisualization,
    useSystemNames: boolean,
    enableAggregation: boolean = true,
    enableErrorBarColumn: boolean = false
  ) {
    this.columnNames = columnNames;
    this.previousVisualization = previousVisualization;
    this.useSystemNames = useSystemNames;
    this.enableAggregation = enableAggregation;
    this.enableErrorBarColumn = enableErrorBarColumn;
  }

  /**
   * Get default parameters required for this type.
   *
   * For example,
   *   - Bar chart: simple, standard bar chart; linear scale; no series; assume 1st column is x and 2nd
   *     column is y (or first column if there is only one column)
   *   - Table: simple table displaying all columns
   */
  abstract getDefaultParameters(): Omit<VisualizationTypeToParameters[TType], '__typename'>;

  /**
   * Convert type to an update to the GraphQL AnalyticsVisualization.
   *
   * This involves updating AnalyticsVisualization parameters by providing appropriate __typename
   * and getting other parameters from getParameters().
   *
   */
  abstract getUpdate(): Update<VisualizationTypeToParameters[TType]>;

  /**
   * Generate parameters using defaults for the current visualization type and preserve values of shared
   * parameters from the previous visualization type.
   *
   */
  getParameters(): Omit<VisualizationTypeToParameters[TType], '__typename'> {
    const currentVisualizationDefaultParameters = this.getDefaultParameters();
    const previousVisualizationParameters = this.previousVisualization.parameters;
    // We need to suppress TypeScript because we don't know the type of previousVisualizationParameters.
    // Final result is still typesafe since currentVisualizationDefaultParameters is typesafe and we are
    // only picking the relevant parameters for the current visualization type.
    const relevantPreviousParameters = pick(
      previousVisualizationParameters,
      // @ts-expect-error
      ...Object.keys(currentVisualizationDefaultParameters)
    );
    return {
      ...currentVisualizationDefaultParameters,
      ...relevantPreviousParameters,
    };
  }

  getColumnDisplayName(index: number): string {
    return this.columnNames[index].displayName;
  }

  getColumnNameForConfigSettingAtIndex(index: number): string {
    return this.useSystemNames ? this.columnNames[index].systemName : this.columnNames[index].displayName;
  }
}

abstract class BaseChartVisualizationSetter<
  TType extends VisualizationType,
> extends VisualizationTypeSetter<TType> {
  getBaseDefaultParameters(): BaseChartParameters {
    return {
      seriesColumnName: null,
      xAxisColumnName: this.getColumnNameForConfigSettingAtIndex(0),
      yAxisColumnName: this.getColumnNameForConfigSettingAtIndex(this.columnNames.length > 1 ? 1 : 0),
      yAxisScale: AxisScale.LINEAR,
    };
  }
}

abstract class Continuous2DChartSetter<
  TType extends VisualizationType,
> extends BaseChartVisualizationSetter<TType> {
  getContinuous2DDefaultParameters(): Continuous2DChartParameters {
    return {
      xAxisScale: AxisScale.LINEAR,
      yAxisIncludesZero: true,
      ...super.getBaseDefaultParameters(),
    };
  }
}

class BarChartSetter extends BaseChartVisualizationSetter<VisualizationType.BAR_CHART> {
  getDefaultParameters(): Omit<BarChartParameters, '__typename'> {
    const parameters: Omit<BarChartParameters, '__typename'> = {
      aggregationType: null,
      barChartType: BarChartType.STANDARD,
      errorBarType: null,
      errorBarColumnName: null,
      yAxisMax: null,
      yAxisMin: null,
      sortDirection: null,
      ...super.getBaseDefaultParameters(),
    };

    if (!this.enableErrorBarColumn) {
      delete parameters.errorBarColumnName;
    }

    return parameters;
  }

  override getParameters(): Omit<BarChartParameters, '__typename'> {
    const parameters = super.getParameters();
    const parametersFromPieChart = isVisualizationTypeParameters(
      this.previousVisualization.parameters,
      VisualizationType.PIE_CHART
    )
      ? {
          xAxisColumnName: this.previousVisualization.parameters.sliceColumnName,
          yAxisColumnName: this.previousVisualization.parameters.measureColumnName,
        }
      : {};

    return {
      ...parameters,
      ...parametersFromPieChart,
    };
  }

  getUpdate(): Update<BarChartParameters> {
    return {
      parameters: {
        __typename: 'AnalyticsVisualizationBarChartParametersNode',
        ...this.getParameters(),
      },
    };
  }
}

class HeatmapSetter extends VisualizationTypeSetter<VisualizationType.HEATMAP> {
  getDefaultParameters(): Omit<HeatmapParameters, '__typename'> {
    return {
      gradientDirection: GradientDirection.ASCENDING_METRIC_VALUES,
      metricColumnName: this.getColumnNameForConfigSettingAtIndex(clamp(0, this.columnNames.length - 1, 2)),
      xAxisColumnName: this.getColumnNameForConfigSettingAtIndex(0),
      yAxisColumnName: this.getColumnNameForConfigSettingAtIndex(clamp(0, this.columnNames.length - 1, 1)),
    };
  }

  getUpdate(): Update<HeatmapParameters> {
    return {
      parameters: {
        __typename: 'AnalyticsVisualizationHeatmapParametersNode',
        ...this.getParameters(),
      },
    };
  }
}

class LineChartSetter extends Continuous2DChartSetter<VisualizationType.LINE_CHART> {
  getDefaultParameters(): Omit<LineChartParameters, '__typename'> {
    const parameters: Omit<LineChartParameters, '__typename'> = {
      ...super.getContinuous2DDefaultParameters(),
      errorBarColumnName: null,
    };

    if (!this.enableErrorBarColumn) {
      delete parameters.errorBarColumnName;
    }

    return parameters;
  }

  getUpdate(): Update<LineChartParameters> {
    return {
      parameters: {
        __typename: 'AnalyticsVisualizationLineChartParametersNode',
        ...this.getParameters(),
      },
    };
  }
}

class PieChartSetter extends VisualizationTypeSetter<VisualizationType.PIE_CHART> {
  DEFAULT_AGGREGATION_TYPE: AggregationType = AggregationType.COUNT;
  // In contexts where aggregation is not a chart setting, default to SUM in order to enable
  // selection of measure column (which is disabled if aggregation is COUNT)
  NON_AGGREGABLE_AGGREGATION_TYPE: AggregationType = AggregationType.SUM;

  getDefaultParameters(): Omit<PieChartParameters, '__typename'> {
    return {
      aggregationType: this.enableAggregation
        ? this.DEFAULT_AGGREGATION_TYPE
        : this.NON_AGGREGABLE_AGGREGATION_TYPE,
      legendLocation: LegendLocation.RIGHT,
      measureColumnName: this.getColumnNameForConfigSettingAtIndex(0),
      sliceColumnName: this.getColumnNameForConfigSettingAtIndex(0),
      showPercentageInLabel: true,
      showSliceNameInLabel: true,
      showValueInLabel: false,
    };
  }

  override getParameters(): Omit<PieChartParameters, '__typename'> {
    const parameters = super.getParameters();
    const parametersFromBarChart = isVisualizationTypeParameters(
      this.previousVisualization.parameters,
      VisualizationType.BAR_CHART
    )
      ? {
          sliceColumnName: this.previousVisualization.parameters.xAxisColumnName,
          measureColumnName: this.previousVisualization.parameters.yAxisColumnName,
        }
      : {};

    return {
      ...parameters,
      ...parametersFromBarChart,
    };
  }

  getUpdate(): Update<PieChartParameters> {
    const COMPATIBLE_PIE_CHART_LOCATIONS = [LegendLocation.LEFT, LegendLocation.RIGHT, null];
    const parameters = this.getParameters();
    const legendLocation = COMPATIBLE_PIE_CHART_LOCATIONS.includes(parameters.legendLocation)
      ? parameters.legendLocation
      : LegendLocation.RIGHT;

    // In some other chart types, aggregationType is optional, which means that we have to be careful here
    // to swap in a default value for aggregationType when coming from other chart types or else we will
    // get a settings editor in an invalid state when transitioning from e.g. scatter plot with
    // aggregationType = None to a pie chart.
    return {
      parameters: {
        __typename: 'AnalyticsVisualizationPieChartParametersNode',
        ...parameters,
        aggregationType: this.enableAggregation
          ? (parameters.aggregationType ?? this.DEFAULT_AGGREGATION_TYPE)
          : this.NON_AGGREGABLE_AGGREGATION_TYPE,
        legendLocation: legendLocation ?? null,
      },
    };
  }
}

class ScatterPlotSetter extends Continuous2DChartSetter<VisualizationType.SCATTER_PLOT> {
  getDefaultParameters(): Omit<ScatterPlotParameters, '__typename'> {
    const parameters: Omit<ScatterPlotParameters, '__typename'> = {
      aggregationType: null,
      colorPalette: SCATTER_PLOT_DEFAULT_COLOR_PALETTE,
      errorBarType: null,
      errorBarColumnName: null,
      legendLocation: LegendLocation.TOP,
      outlierConfig: null,
      regressionConfig: null,
      rowIdColumnName: null,
      yAxisMax: null,
      yAxisMin: null,
      ...super.getContinuous2DDefaultParameters(),
    };

    if (!this.enableErrorBarColumn) {
      delete parameters.errorBarColumnName;
    }

    return parameters;
  }

  getUpdate(): Update<ScatterPlotParameters> {
    const COMPATIBLE_SCATTER_PLOT_LOCATIONS = [LegendLocation.TOP, null];
    const newParameters = this.getParameters();
    const legendLocation = COMPATIBLE_SCATTER_PLOT_LOCATIONS.includes(newParameters.legendLocation)
      ? newParameters.legendLocation
      : LegendLocation.TOP;

    return {
      parameters: {
        __typename: 'AnalyticsVisualizationScatterPlotParametersNode',
        ...newParameters,
        legendLocation: newParameters.seriesColumnName != null ? legendLocation : null,
        regressionConfig: newParameters.seriesColumnName == null ? newParameters.regressionConfig : null,
      },
    };
  }
}

class TableSetter extends VisualizationTypeSetter<VisualizationType.TABLE> {
  getDefaultParameters(): Omit<TableParameters, '__typename'> {
    return {displayColumnsJson: null};
  }

  getUpdate(): Update<TableParameters> {
    return {
      parameters: {
        __typename: 'AnalyticsVisualizationTableParametersNode',
        ...this.getParameters(),
      },
    };
  }
}

export const VISUALIZATION_TYPE_SETTERS: {
  [k in VisualizationType]: new (
    columnNames: Array<DataFrameColumnDef>,
    previousVisualization: AnalyticsVisualization,
    useSystemNames: boolean,
    enableAggregation?: boolean,
    enableErrorBarColumn?: boolean
  ) => VisualizationTypeSetter<k>;
} = {
  [VisualizationType.BAR_CHART]: BarChartSetter,
  [VisualizationType.HEATMAP]: HeatmapSetter,
  [VisualizationType.LINE_CHART]: LineChartSetter,
  [VisualizationType.PIE_CHART]: PieChartSetter,
  [VisualizationType.SCATTER_PLOT]: ScatterPlotSetter,
  [VisualizationType.TABLE]: TableSetter,
};
